import {CSV_MODE_DEFAULT} from "../../components/etl-wizard/constants";
import {
  getAgentSchemaUrl,
  getAgentUrl,
  getCkanGroupUrl,
  getCkanOrganizationUrl,
  getCodelistUrl,
  getEtlResourceUrl,
  getImportDataUrl,
  getNodeCatalogUrl,
  getPackageLicenseSchemaUrl,
  getPackageLicenseUrl,
  getPackagePermissionsUrl,
  getPackageSchemaUrl,
  getPackageUrl,
  getResourceLicenseSchemaUrl,
  getResourceLicenseUrl,
  getResourceSchemaUrl,
  getResourceUrl,
  getStructuredDataDefinitionUrl,
  getStructuredDataSuggestionsUrl,
  getSupersetDependenciesUrl,
  getThemeSchemaUrl,
  getThemeUrl
} from "../../constants/apiEndpoints";
import {NODE_TYPE_CKAN} from "../../constants/nodes";
import {initRequest, RequestMethod} from "../../middlewares/request/requestActions";

export const PACKAGE_MANAGER_STATE_CLEAR = "packageManager/state/clear";
export const PACKAGE_MANAGER_CATALOG_RELOAD = "packageManager/catalog/reload";

export const clearPackageManagerState = () => ({
  type: PACKAGE_MANAGER_STATE_CLEAR
});

export const reloadCatalog = () => ({
  type: PACKAGE_MANAGER_CATALOG_RELOAD
});

/* Packages */
export const PACKAGE_MANAGER_PACKAGE_LIST_SPINNER = "packageManager/packageList/spinner";
export const PACKAGE_MANAGER_PACKAGE_LIST_SHOW = "packageManager/packageList/show";
export const PACKAGE_MANAGER_PACKAGE_LIST_HIDE = "packageManager/packageList/hide";
export const PACKAGE_MANAGER_PACKAGE_LIST_FETCH = "packageManager/packageList/fetch";
export const PACKAGE_MANAGER_PACKAGE_OWNERSHIP_FETCH = "packageManager/packageOwnership/fetch";
export const PACKAGE_MANAGER_PACKAGE_OWNERSHIP_SET = "packageManager/packageOwnership/set";
export const PACKAGE_MANAGER_PACKAGE_SCHEMA_FETCH = "packageManager/packageSchema/fetch";
export const PACKAGE_MANAGER_PACKAGE_SHOW = "packageManager/package/show";
export const PACKAGE_MANAGER_PACKAGE_GET = "packageManager/package/get";
export const PACKAGE_MANAGER_PACKAGE_HIDE = "packageManager/package/hide";
export const PACKAGE_MANAGER_PACKAGE_CREATE = "packageManager/package/create";
export const PACKAGE_MANAGER_PACKAGE_UPDATE = "packageManager/package/update";
export const PACKAGE_MANAGER_PACKAGE_DELETE = "packageManager/package/delete";
export const PACKAGE_MANAGER_PACKAGE_APPROVE = "packageManager/package/approve";
export const PACKAGE_MANAGER_PACKAGE_DISAPPROVE = "packageManager/package/disapprove";

export const showPackageList = (nodeId, nodeCode, readOnly) => ({
  type: PACKAGE_MANAGER_PACKAGE_LIST_SHOW,
  payload: {
    nodeId: nodeId,
    nodeCode: nodeCode,
    readOnly: readOnly
  }
});

export const hidePackageList = () => ({
  type: PACKAGE_MANAGER_PACKAGE_LIST_HIDE
});

export const fetchPackageList = nodeId => initRequest(PACKAGE_MANAGER_PACKAGE_LIST_FETCH, getNodeCatalogUrl(nodeId));

export const fetchPackageOwnership = nodeId =>
  initRequest(PACKAGE_MANAGER_PACKAGE_OWNERSHIP_FETCH, getPackageUrl(nodeId));

export const setPackageOwnership = () => ({
  type: PACKAGE_MANAGER_PACKAGE_OWNERSHIP_SET
});

export const getPackageSchema = nodeId =>
  initRequest(PACKAGE_MANAGER_PACKAGE_SCHEMA_FETCH, getPackageSchemaUrl(nodeId), RequestMethod.GET, undefined, t => ({
    onStart: t("modules.packageManager.actions.messages.getPackageSchema")
  }));

export const showPackage = packageId => ({
  type: PACKAGE_MANAGER_PACKAGE_SHOW,
  payload: {
    packageId
  }
});

export const getPackage = (nodeId, datasetId) =>
  initRequest(
    PACKAGE_MANAGER_PACKAGE_GET,
    getPackageUrl(nodeId, datasetId, {useRelativePaths: true}),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getPackage")
    })
  );

export const hidePackage = () => ({
  type: PACKAGE_MANAGER_PACKAGE_HIDE
});

export const createPackage = (nodeId, dataset) => {
  const formData = new FormData();
  formData.append("json", JSON.stringify(dataset));

  return initRequest(PACKAGE_MANAGER_PACKAGE_CREATE, getPackageUrl(nodeId), RequestMethod.POST, formData, t => ({
    onStart: t("modules.packageManager.actions.messages.createPackage")
  }));
};

export const updatePackage = (nodeId, datasetId, dataset) => {
  const formData = new FormData();
  formData.append("json", JSON.stringify(dataset));

  return initRequest(
    PACKAGE_MANAGER_PACKAGE_UPDATE,
    getPackageUrl(nodeId, datasetId),
    RequestMethod.PUT,
    formData,
    t => ({
      onStart: t("modules.packageManager.actions.messages.updatePackage")
    })
  );
};

export const deletePackage = (nodeId, datasetId) =>
  initRequest(PACKAGE_MANAGER_PACKAGE_DELETE, getPackageUrl(nodeId, datasetId), RequestMethod.DELETE, undefined, t => ({
    onStart: t("modules.packageManager.actions.messages.deletePackage")
  }));

export const approvePackage = (nodeId, datasetId) =>
  initRequest(
    PACKAGE_MANAGER_PACKAGE_APPROVE,
    getPackagePermissionsUrl(nodeId),
    RequestMethod.PUT,
    [
      {
        entityType: "Package",
        entityIdentifier: datasetId,
        remove: true
      }
    ],
    t => ({
      onStart: t("modules.packageManager.actions.messages.approvePackage")
    }),
    {
      nodeId: nodeId
    }
  );

export const disapprovePackage = (nodeId, datasetId) =>
  initRequest(
    PACKAGE_MANAGER_PACKAGE_DISAPPROVE,
    getPackagePermissionsUrl(nodeId),
    RequestMethod.PUT,
    [
      {
        entityType: "Package",
        entityIdentifier: datasetId,
        remove: false
      }
    ],
    t => ({
      onStart: t("modules.packageManager.actions.messages.disapprovePackage")
    }),
    {
      nodeId: nodeId
    }
  );

/* Resources */

export const PACKAGE_MANAGER_RESOURCE_LIST_SHOW = "packageManager/resourceList/show";
export const PACKAGE_MANAGER_RESOURCE_LIST_HIDE = "packageManager/resourceList/hide";
export const PACKAGE_MANAGER_RESOURCE_SCHEMA_FETCH = "packageManager/resourceSchema/fetch";
export const PACKAGE_MANAGER_RESOURCE_SHOW = "packageManager/resource/show";
export const PACKAGE_MANAGER_RESOURCE_GET = "packageManager/resource/get";
export const PACKAGE_MANAGER_RESOURCE_HIDE = "packageManager/resource/hide";
export const PACKAGE_MANAGER_RESOURCE_CREATE = "packageManager/resource/create";
export const PACKAGE_MANAGER_RESOURCE_UPDATE = "packageManager/resource/update";
export const PACKAGE_MANAGER_RESOURCE_DELETE = "packageManager/resource/delete";

export const showResourceList = packageId => ({
  type: PACKAGE_MANAGER_RESOURCE_LIST_SHOW,
  payload: {
    packageId
  }
});

export const hideResourceList = () => ({
  type: PACKAGE_MANAGER_RESOURCE_LIST_HIDE
});

export const getResourceSchema = nodeId =>
  initRequest(PACKAGE_MANAGER_RESOURCE_SCHEMA_FETCH, getResourceSchemaUrl(nodeId), RequestMethod.GET, undefined, t => ({
    onStart: t("modules.packageManager.actions.messages.getResourceSchema")
  }));

export const showResource = resourceId => ({
  type: PACKAGE_MANAGER_RESOURCE_SHOW,
  payload: {
    resourceId
  }
});

export const getResource = (nodeId, resourceId) =>
  initRequest(
    PACKAGE_MANAGER_RESOURCE_GET,
    getResourceUrl(nodeId, resourceId, {useRelativePaths: true}),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getResource")
    })
  );

export const hideResource = () => ({
  type: PACKAGE_MANAGER_RESOURCE_HIDE
});

export const createResource = (nodeId, resource) => {
  const formData = new FormData();
  formData.append("json", JSON.stringify(resource));
  formData.append("files", resource.content);

  return initRequest(PACKAGE_MANAGER_RESOURCE_CREATE, getResourceUrl(nodeId), RequestMethod.POST, formData, t => ({
    onStart: t("modules.packageManager.actions.messages.createResource")
  }));
};

export const updateResource = (nodeId, resourceId, resource) => {
  const formData = new FormData();
  formData.append("json", JSON.stringify(resource));
  formData.append("files", resource.content);

  return initRequest(
    PACKAGE_MANAGER_RESOURCE_UPDATE,
    getResourceUrl(nodeId, resourceId),
    RequestMethod.PUT,
    formData,
    t => ({
      onStart: t("modules.packageManager.actions.messages.updateResource")
    })
  );
};

export const deleteResource = (nodeId, resourceId) =>
  initRequest(
    PACKAGE_MANAGER_RESOURCE_DELETE,
    getResourceUrl(nodeId, resourceId),
    RequestMethod.DELETE,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.deleteResource")
    })
  );

/* Agents */

export const PACKAGE_MANAGER_AGENT_LIST_SHOW = "packageManager/agentList/show";
export const PACKAGE_MANAGER_AGENT_LIST_HIDE = "packageManager/agentList/hide";
export const PACKAGE_MANAGER_AGENT_LIST_FETCH = "packageManager/agentList/fetch";
export const PACKAGE_MANAGER_AGENT_SCHEMA_FETCH = "packageManager/agent/schema/fetch";
export const PACKAGE_MANAGER_AGENT_SHOW = "packageManager/agent/show";
export const PACKAGE_MANAGER_AGENT_GET = "packageManager/agent/get";
export const PACKAGE_MANAGER_AGENT_HIDE = "packageManager/agent/hide";
export const PACKAGE_MANAGER_AGENT_CREATE = "packageManager/agent/create";
export const PACKAGE_MANAGER_AGENT_UPDATE = "packageManager/agent/update";
export const PACKAGE_MANAGER_AGENT_DELETE = "packageManager/agent/delete";

export const showAgentList = () => ({
  type: PACKAGE_MANAGER_AGENT_LIST_SHOW
});

export const hideAgentList = () => ({
  type: PACKAGE_MANAGER_AGENT_LIST_HIDE
});

export const fetchAgentList = nodeId =>
  initRequest(
    PACKAGE_MANAGER_AGENT_LIST_FETCH,
    getAgentUrl(nodeId, undefined, {useStub: true}),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getAgents")
    })
  );

export const fetchAgentSchema = nodeId =>
  initRequest(PACKAGE_MANAGER_AGENT_SCHEMA_FETCH, getAgentSchemaUrl(nodeId), RequestMethod.GET, undefined, t => ({
    onStart: t("modules.packageManager.actions.messages.getAgentSchema")
  }));

export const showAgent = () => ({
  type: PACKAGE_MANAGER_AGENT_SHOW
});

export const getAgent = (nodeId, agentId, agentTitle) =>
  initRequest(
    PACKAGE_MANAGER_AGENT_GET,
    getAgentUrl(nodeId, agentId, {useRelativePaths: true}),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getAgent", {title: agentTitle})
    }),
    {
      agentId: agentId
    }
  );

export const hideAgent = () => ({
  type: PACKAGE_MANAGER_AGENT_HIDE
});

export const createAgent = (nodeId, agent) => {
  const formData = new FormData();
  formData.append("json", JSON.stringify(agent));

  return initRequest(PACKAGE_MANAGER_AGENT_CREATE, getAgentUrl(nodeId), RequestMethod.POST, formData, t => ({
    onStart: t("modules.packageManager.actions.messages.createAgent")
  }));
};

export const updateAgent = (nodeId, agentId, agent) => {
  const formData = new FormData();
  formData.append("json", JSON.stringify(agent));

  return initRequest(PACKAGE_MANAGER_AGENT_UPDATE, getAgentUrl(nodeId, agentId), RequestMethod.PUT, formData, t => ({
    onStart: t("modules.packageManager.actions.messages.updateAgent")
  }));
};

export const deleteAgent = (nodeId, agentId, agentTitle) =>
  initRequest(PACKAGE_MANAGER_AGENT_DELETE, getAgentUrl(nodeId, agentId), RequestMethod.DELETE, undefined, t => ({
    onStart: t("modules.packageManager.actions.messages.deleteAgent", {title: agentTitle})
  }));

/* Themes */

export const PACKAGE_MANAGER_THEME_LIST_SHOW = "packageManager/themeList/show";
export const PACKAGE_MANAGER_THEME_LIST_HIDE = "packageManager/themeList/hide";
export const PACKAGE_MANAGER_THEME_LIST_FETCH = "packageManager/themeList/fetch";
export const PACKAGE_MANAGER_THEME_SCHEMA_FETCH = "packageManager/theme/schema/fetch";
export const PACKAGE_MANAGER_THEME_SHOW = "packageManager/theme/show";
export const PACKAGE_MANAGER_THEME_GET = "packageManager/theme/get";
export const PACKAGE_MANAGER_THEME_HIDE = "packageManager/theme/hide";
export const PACKAGE_MANAGER_THEME_CREATE = "packageManager/theme/create";
export const PACKAGE_MANAGER_THEME_UPDATE = "packageManager/theme/update";
export const PACKAGE_MANAGER_THEME_DELETE = "packageManager/theme/delete";

export const showThemeList = () => ({
  type: PACKAGE_MANAGER_THEME_LIST_SHOW
});

export const hideThemeList = () => ({
  type: PACKAGE_MANAGER_THEME_LIST_HIDE
});

export const fetchThemeList = nodeId =>
  initRequest(
    PACKAGE_MANAGER_THEME_LIST_FETCH,
    getThemeUrl(nodeId, undefined, {useStub: true}),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getThemes")
    })
  );

export const fetchThemeSchema = nodeId =>
  initRequest(PACKAGE_MANAGER_THEME_SCHEMA_FETCH, getThemeSchemaUrl(nodeId), RequestMethod.GET, undefined, t => ({
    onStart: t("modules.packageManager.actions.messages.getThemeSchema")
  }));

export const showTheme = () => ({
  type: PACKAGE_MANAGER_THEME_SHOW
});

export const getTheme = (nodeId, themeId, themeTitle) =>
  initRequest(
    PACKAGE_MANAGER_THEME_GET,
    getThemeUrl(nodeId, themeId, {useRelativePaths: true}),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getTheme", {title: themeTitle})
    }),
    {
      themeId: themeId
    }
  );

export const hideTheme = () => ({
  type: PACKAGE_MANAGER_THEME_HIDE
});

export const createTheme = (nodeId, theme) => {
  const formData = new FormData();
  formData.append("json", JSON.stringify(theme));

  return initRequest(PACKAGE_MANAGER_THEME_CREATE, getThemeUrl(nodeId), RequestMethod.POST, formData, t => ({
    onStart: t("modules.packageManager.actions.messages.createTheme")
  }));
};

export const updateTheme = (nodeId, themeId, theme) => {
  const formData = new FormData();
  formData.append("json", JSON.stringify(theme));

  return initRequest(PACKAGE_MANAGER_THEME_UPDATE, getThemeUrl(nodeId, themeId), RequestMethod.PUT, formData, t => ({
    onStart: t("modules.packageManager.actions.messages.updateTheme")
  }));
};

export const deleteTheme = (nodeId, themeId, themeTitle) =>
  initRequest(PACKAGE_MANAGER_THEME_DELETE, getThemeUrl(nodeId, themeId), RequestMethod.DELETE, undefined, t => ({
    onStart: t("modules.packageManager.actions.messages.deleteTheme", {title: themeTitle})
  }));

/* PackageLicenses */

export const PACKAGE_MANAGER_PACKAGE_LICENSE_LIST_SHOW = "packageManager/packageLicenseList/show";
export const PACKAGE_MANAGER_PACKAGE_LICENSE_LIST_HIDE = "packageManager/packageLicenseList/hide";
export const PACKAGE_MANAGER_PACKAGE_LICENSE_LIST_FETCH = "packageManager/packageLicenseList/fetch";
export const PACKAGE_MANAGER_PACKAGE_LICENSE_SCHEMA_FETCH = "packageManager/packageLicense/schema/fetch";
export const PACKAGE_MANAGER_PACKAGE_LICENSE_SHOW = "packageManager/packageLicense/show";
export const PACKAGE_MANAGER_PACKAGE_LICENSE_GET = "packageManager/packageLicense/get";
export const PACKAGE_MANAGER_PACKAGE_LICENSE_HIDE = "packageManager/packageLicense/hide";
export const PACKAGE_MANAGER_PACKAGE_LICENSE_CREATE = "packageManager/packageLicense/create";
export const PACKAGE_MANAGER_PACKAGE_LICENSE_UPDATE = "packageManager/packageLicense/update";
export const PACKAGE_MANAGER_PACKAGE_LICENSE_DELETE = "packageManager/packageLicense/delete";

export const showPackageLicenseList = () => ({
  type: PACKAGE_MANAGER_PACKAGE_LICENSE_LIST_SHOW
});

export const hidePackageLicenseList = () => ({
  type: PACKAGE_MANAGER_PACKAGE_LICENSE_LIST_HIDE
});

export const fetchPackageLicenseList = nodeId =>
  initRequest(
    PACKAGE_MANAGER_PACKAGE_LICENSE_LIST_FETCH,
    getPackageLicenseUrl(nodeId, undefined, {useStub: true}),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getPackageLicenses")
    })
  );

export const fetchPackageLicenseSchema = nodeId =>
  initRequest(
    PACKAGE_MANAGER_PACKAGE_LICENSE_SCHEMA_FETCH,
    getPackageLicenseSchemaUrl(nodeId),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getPackageLicenseSchema")
    })
  );

export const showPackageLicense = () => ({
  type: PACKAGE_MANAGER_PACKAGE_LICENSE_SHOW
});

export const getPackageLicense = (nodeId, packageLicenseId, packageLicenseTitle) =>
  initRequest(
    PACKAGE_MANAGER_PACKAGE_LICENSE_GET,
    getPackageLicenseUrl(nodeId, packageLicenseId, {useRelativePaths: true}),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getPackageLicense", {title: packageLicenseTitle})
    }),
    {
      packageLicenseId: packageLicenseId
    }
  );

export const hidePackageLicense = () => ({
  type: PACKAGE_MANAGER_PACKAGE_LICENSE_HIDE
});

export const createPackageLicense = (nodeId, packageLicense) => {
  const formData = new FormData();
  formData.append("json", JSON.stringify(packageLicense));

  return initRequest(
    PACKAGE_MANAGER_PACKAGE_LICENSE_CREATE,
    getPackageLicenseUrl(nodeId),
    RequestMethod.POST,
    formData,
    t => ({
      onStart: t("modules.packageManager.actions.messages.createPackageLicense")
    })
  );
};

export const updatePackageLicense = (nodeId, packageLicenseId, packageLicense) => {
  const formData = new FormData();
  formData.append("json", JSON.stringify(packageLicense));

  return initRequest(
    PACKAGE_MANAGER_PACKAGE_LICENSE_UPDATE,
    getPackageLicenseUrl(nodeId, packageLicenseId),
    RequestMethod.PUT,
    formData,
    t => ({
      onStart: t("modules.packageManager.actions.messages.updatePackageLicense")
    })
  );
};

export const deletePackageLicense = (nodeId, packageLicenseId, packageLicenseTitle) =>
  initRequest(
    PACKAGE_MANAGER_PACKAGE_LICENSE_DELETE,
    getPackageLicenseUrl(nodeId, packageLicenseId),
    RequestMethod.DELETE,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.deletePackageLicense", {title: packageLicenseTitle})
    })
  );

/* ResourceLicenses */

export const PACKAGE_MANAGER_RESOURCE_LICENSE_LIST_SHOW = "packageManager/resourceLicenseList/show";
export const PACKAGE_MANAGER_RESOURCE_LICENSE_LIST_HIDE = "packageManager/resourceLicenseList/hide";
export const PACKAGE_MANAGER_RESOURCE_LICENSE_LIST_FETCH = "packageManager/resourceLicenseList/fetch";
export const PACKAGE_MANAGER_RESOURCE_LICENSE_SCHEMA_FETCH = "packageManager/resourceLicense/schema/fetch";
export const PACKAGE_MANAGER_RESOURCE_LICENSE_SHOW = "packageManager/resourceLicense/show";
export const PACKAGE_MANAGER_RESOURCE_LICENSE_GET = "packageManager/resourceLicense/get";
export const PACKAGE_MANAGER_RESOURCE_LICENSE_HIDE = "packageManager/resourceLicense/hide";
export const PACKAGE_MANAGER_RESOURCE_LICENSE_CREATE = "packageManager/resourceLicense/create";
export const PACKAGE_MANAGER_RESOURCE_LICENSE_UPDATE = "packageManager/resourceLicense/update";
export const PACKAGE_MANAGER_RESOURCE_LICENSE_DELETE = "packageManager/resourceLicense/delete";

export const showResourceLicenseList = () => ({
  type: PACKAGE_MANAGER_RESOURCE_LICENSE_LIST_SHOW
});

export const hideResourceLicenseList = () => ({
  type: PACKAGE_MANAGER_RESOURCE_LICENSE_LIST_HIDE
});

export const fetchResourceLicenseList = nodeId =>
  initRequest(
    PACKAGE_MANAGER_RESOURCE_LICENSE_LIST_FETCH,
    getResourceLicenseUrl(nodeId, undefined, {useStub: true}),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getResourceLicenses")
    })
  );

export const fetchResourceLicenseSchema = nodeId =>
  initRequest(
    PACKAGE_MANAGER_RESOURCE_LICENSE_SCHEMA_FETCH,
    getResourceLicenseSchemaUrl(nodeId),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getResourceLicenseSchema")
    })
  );

export const showResourceLicense = () => ({
  type: PACKAGE_MANAGER_RESOURCE_LICENSE_SHOW
});

export const getResourceLicense = (nodeId, resourceLicenseId, resourceLicenseTitle) =>
  initRequest(
    PACKAGE_MANAGER_RESOURCE_LICENSE_GET,
    getResourceLicenseUrl(nodeId, resourceLicenseId, {useRelativePaths: true}),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getResourceLicense", {title: resourceLicenseTitle})
    }),
    {
      resourceLicenseId: resourceLicenseId
    }
  );

export const hideResourceLicense = () => ({
  type: PACKAGE_MANAGER_RESOURCE_LICENSE_HIDE
});

export const createResourceLicense = (nodeId, resourceLicense) => {
  const formData = new FormData();
  formData.append("json", JSON.stringify(resourceLicense));

  return initRequest(
    PACKAGE_MANAGER_RESOURCE_LICENSE_CREATE,
    getResourceLicenseUrl(nodeId),
    RequestMethod.POST,
    formData,
    t => ({
      onStart: t("modules.packageManager.actions.messages.createResourceLicense")
    })
  );
};

export const updateResourceLicense = (nodeId, resourceLicenseId, resourceLicense) => {
  const formData = new FormData();
  formData.append("json", JSON.stringify(resourceLicense));

  return initRequest(
    PACKAGE_MANAGER_RESOURCE_LICENSE_UPDATE,
    getResourceLicenseUrl(nodeId, resourceLicenseId),
    RequestMethod.PUT,
    formData,
    t => ({
      onStart: t("modules.packageManager.actions.messages.updateResourceLicense")
    })
  );
};

export const deleteResourceLicense = (nodeId, resourceLicenseId, resourceLicenseTitle) =>
  initRequest(
    PACKAGE_MANAGER_RESOURCE_LICENSE_DELETE,
    getResourceLicenseUrl(nodeId, resourceLicenseId),
    RequestMethod.DELETE,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.deleteResourceLicense", {title: resourceLicenseTitle})
    })
  );

/* ETL Wizard */

export const PACKAGE_MANAGER_ETL_WIZARD_MODAL_SHOW = "packageManager/etlWizard/modal/show";
export const PACKAGE_MANAGER_ETL_WIZARD_MODAL_HIDE = "packageManager/etlWizard/modal/hide";
export const PACKAGE_MANAGER_ETL_WIZARD_MODAL_SUBMIT = "packageManager/etlWizard/modal/submit";
export const PACKAGE_MANAGER_ETL_WIZARD_STEP_SET = "packageManager/etlWizard/step/set";
export const PACKAGE_MANAGER_ETL_WIZARD_COLUMNS_SET = "packageManager/etlWizard/columns/set";
export const PACKAGE_MANAGER_ETL_WIZARD_ERROR_SET = "packageManager/etlWizard/error/set";
export const PACKAGE_MANAGER_ETL_WIZARD_CSV_FORMAT_SET = "packageManager/etlWizard/csvFormat/set";
export const PACKAGE_MANAGER_ETL_WIZARD_REPORT_HIDE = "packageManager/etlWizard/report/hide";
export const PACKAGE_MANAGER_ETL_WIZARD_STRUCTURE_GET = "packageManager/etlWizard/structure/get";
export const PACKAGE_MANAGER_ETL_WIZARD_CODELIST_GET = "packageManager/etlWizard/codelist/get";
export const PACKAGE_MANAGER_ETL_WIZARD_RESOURCE_DELETE = "packageManager/etlWizard/resource/delete";

export const showEtlWizardModal = (resourceId, isEditMode = false, nodeId, nodeCode, packageId, packageTitle) => ({
  type: PACKAGE_MANAGER_ETL_WIZARD_MODAL_SHOW,
  payload: {
    resourceId: resourceId,
    isEditMode: isEditMode,
    nodeId: nodeId,
    nodeCode: nodeCode,
    packageId: packageId,
    packageTitle: packageTitle
  }
});

export const hideEtlWizardModal = () => ({
  type: PACKAGE_MANAGER_ETL_WIZARD_MODAL_HIDE
});

export const submitEtlWizard = (etlResource, nodeId, packageId, resourceId, forceImport = false) =>
  initRequest(
    PACKAGE_MANAGER_ETL_WIZARD_MODAL_SUBMIT,
    getImportDataUrl(nodeId, packageId, resourceId, forceImport),
    RequestMethod.POST,
    {...etlResource},
    t => ({
      onStart: t("modules.packageManager.actions.messages.etlSubmit")
    }),
    {
      forceImport: forceImport
    }
  );

export const setEtlWizardStep = step => ({
  type: PACKAGE_MANAGER_ETL_WIZARD_STEP_SET,
  payload: {
    step: step
  }
});

export const setEtlWizardColumns = columns => ({
  type: PACKAGE_MANAGER_ETL_WIZARD_COLUMNS_SET,
  payload: {
    columns: columns
  }
});

export const setEtlWizardErrorMessage = errorMessage => ({
  type: PACKAGE_MANAGER_ETL_WIZARD_ERROR_SET,
  payload: {
    errorMessage: errorMessage
  }
});

export const setEtlWizardCsvFormat = csvFormat => ({
  type: PACKAGE_MANAGER_ETL_WIZARD_CSV_FORMAT_SET,
  payload: {
    csvFormat: csvFormat
  }
});

export const hideEtlWizardReport = () => ({
  type: PACKAGE_MANAGER_ETL_WIZARD_REPORT_HIDE
});

export const getEtlWizardStructure = (nodeId, resourceId, isEditMode, csvFormatOptions) =>
  initRequest(
    PACKAGE_MANAGER_ETL_WIZARD_STRUCTURE_GET,
    isEditMode
      ? getStructuredDataDefinitionUrl(nodeId, resourceId, !Boolean(csvFormatOptions))
      : getStructuredDataSuggestionsUrl(nodeId, resourceId, !Boolean(csvFormatOptions)),
    isEditMode ? RequestMethod.POST : RequestMethod.PUT,
    csvFormatOptions
      ? {
          delimiter: csvFormatOptions.delimiter ?? null,
          quote: csvFormatOptions.quote ?? null,
          escape: csvFormatOptions.escape ?? null,
          csvMode: csvFormatOptions.csvMode ?? CSV_MODE_DEFAULT
        }
      : {},
    t => ({
      onStart: t("modules.packageManager.actions.messages.getEtlStructure")
    })
  );

export const getEtlWizardCodelist = nodeId =>
  initRequest(PACKAGE_MANAGER_ETL_WIZARD_CODELIST_GET, getCodelistUrl(nodeId), RequestMethod.GET, undefined, t => ({
    onStart: t("modules.packageManager.actions.messages.getEtlCodelist")
  }));

export const deleteEtlResource = (resource, nodeId, onDeleteEnd) =>
  initRequest(
    PACKAGE_MANAGER_ETL_WIZARD_RESOURCE_DELETE,
    getEtlResourceUrl(nodeId, resource),
    RequestMethod.DELETE,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.etlDelete")
    }),
    {
      onDeleteEnd: onDeleteEnd
    }
  );

/* Easy Loading */

export const PACKAGE_MANAGER_EASY_LOADING_THEMES_SPINNER = "packageManager/easyLoading/themes/spiner";
export const PACKAGE_MANAGER_EASY_LOADING_THEMES_GET = "packageManager/easyLoading/themes/get";
export const PACKAGE_MANAGER_EASY_LOADING_ORGANIZATIONS_GET = "packageManager/easyLoading/organizations/get";
export const PACKAGE_MANAGER_EASY_LOADING_PACKAGE_LICENSE_GET = "packageManager/easyLoading/packageLicense/get";
export const PACKAGE_MANAGER_EASY_LOADING_RESOURCE_LICENSE_GET = "packageManager/easyLoading/resourceLicense/get";
export const PACKAGE_MANAGER_EASY_LOADING_DATASET_SET = "packageManager/easyLoading/dataset/set";
export const PACKAGE_MANAGER_EASY_LOADING_RESOURCE_SET = "packageManager/easyLoading/resource/set";
export const PACKAGE_MANAGER_EASY_LOADING_SUBMIT = "packageManager/easyLoading/submit/start";
export const PACKAGE_MANAGER_EASY_LOADING_SUBMIT_SUCCESS = "packageManager/easyLoading/submit/success";
export const PACKAGE_MANAGER_EASY_LOADING_SUBMIT_ERROR = "packageManager/easyLoading/submit/error";

export const getEasyLoadingThemes = (nodeId, nodeType) =>
  initRequest(
    PACKAGE_MANAGER_EASY_LOADING_THEMES_GET,
    nodeType === NODE_TYPE_CKAN ? getCkanGroupUrl(nodeId) : getThemeUrl(nodeId, undefined, {useStub: true}),
    RequestMethod.GET
  );

export const getEasyLoadingOrganizations = (nodeId, nodeType) =>
  initRequest(
    PACKAGE_MANAGER_EASY_LOADING_ORGANIZATIONS_GET,
    nodeType === NODE_TYPE_CKAN ? getCkanOrganizationUrl(nodeId) : getAgentUrl(nodeId, undefined, {useStub: true}),
    RequestMethod.GET
  );

export const getEasyLoadingPackageLicense = nodeId =>
  initRequest(
    PACKAGE_MANAGER_EASY_LOADING_PACKAGE_LICENSE_GET,
    getPackageLicenseUrl(nodeId, undefined, {useStub: true}),
    RequestMethod.GET
  );

export const getEasyLoadingResourceLicense = nodeId =>
  initRequest(
    PACKAGE_MANAGER_EASY_LOADING_RESOURCE_LICENSE_GET,
    getResourceLicenseUrl(nodeId, undefined, {useStub: true}),
    RequestMethod.GET
  );

export const setEasyLoadingDataset = dataset => ({
  type: PACKAGE_MANAGER_EASY_LOADING_DATASET_SET,
  payload: {
    dataset: dataset
  }
});

export const setEasyLoadingResource = resource => ({
  type: PACKAGE_MANAGER_EASY_LOADING_RESOURCE_SET,
  payload: {
    resource: resource
  }
});

export const submitEasyLoading = (nodeId, nodeType, dataset, resource) => ({
  type: PACKAGE_MANAGER_EASY_LOADING_SUBMIT,
  payload: {
    nodeId: nodeId,
    nodeType: nodeType,
    dataset: dataset,
    resource: resource
  }
});

export const submitEasyLoadingSuccess = (packageId, resourceId) => ({
  type: PACKAGE_MANAGER_EASY_LOADING_SUBMIT_SUCCESS,
  payload: {
    packageId: packageId,
    resourceId: resourceId
  }
});

export const submitEasyLoadingError = () => ({
  type: PACKAGE_MANAGER_EASY_LOADING_SUBMIT_ERROR
});

/* Easy WMS Loading */

export const PACKAGE_MANAGER_EASY_LOADING_WMS_MODAL_SHOW = "packageManager/easyLoading/wms/modal/show";
export const PACKAGE_MANAGER_EASY_LOADING_WMS_MODAL_HIDE = "packageManager/easyLoading/wms/modal/hide";

export const showEasyLoadingWmsModal = () => ({
  type: PACKAGE_MANAGER_EASY_LOADING_WMS_MODAL_SHOW
});

export const hideEasyLoadingWmsModal = () => ({
  type: PACKAGE_MANAGER_EASY_LOADING_WMS_MODAL_HIDE
});

/* Superset */

export const PACKAGE_MANAGER_SUPERSET_DEPENDENCIES_GET = "packageManager/superset/dependencies/get";

export const getSupersetDependencies = (nodeId, resourceId, onDependenciesFound, onNoDependencies) =>
  initRequest(
    PACKAGE_MANAGER_SUPERSET_DEPENDENCIES_GET,
    getSupersetDependenciesUrl(nodeId, resourceId),
    RequestMethod.GET,
    undefined,
    t => ({
      onStart: t("modules.packageManager.actions.messages.getSupersetDependecies")
    }),
    {
      resourceId: resourceId,
      onDependenciesFound: onDependenciesFound,
      onNoDependencies: onNoDependencies
    }
  );
